/**
 * Embedded files: listing the ones a document carries, reading one out, and the writes that
 * add or remove them.
 *
 * The feature owns no state of its own — the busy gate and the status line are the core's, the
 * tabs are the session's, the engine handle is `core/handles.ts`'s — so this module is the
 * handlers alone. They take what the shell still holds (the translator and the shared writer
 * pipeline) as `AttachmentDeps`, and read the active tab, its handle and the busy gate **at call time**.
 */

import type { OperationOutcome } from 'pdf-core';
import { listPdfAttachments, readPdfAttachment } from 'pdf-core/attachments';
import type { PdfDocumentHandle } from 'pdf-core/engines/pdfjs-handle';
import { type SessionStore, type SessionTab, workingPageCount } from 'pdf-model';
import type { MessageKey, Translator } from 'pdf-shared';
import type { AttachmentRow } from 'pdf-ui';
import { addAttachments, removeAttachments } from '../../lazy-ops';
import { applyProducedBytes, downloadFiles, materializeBase } from '../../operations';
import { showNotice } from '../core/core-store';
import { documentContext } from '../core/document';
import { failureLine, runGatedOperation } from '../core/gated-operation';
import { handleFor, swapHandle } from '../core/handles';

/** What the shell still holds that the attachment handlers run on. */
export interface AttachmentDeps {
  readonly session: SessionStore;
  readonly t: Translator;
  /**
   * The shared writer pipeline: journal the outcome, swap the handle, say what it did. `signal`
   * is the running operation's, so a Cancel keeps the bytes out of the session.
   */
  readonly applyWriterOutcome: (
    tab: SessionTab,
    handle: PdfDocumentHandle,
    outcome: OperationOutcome,
    labelKey: MessageKey,
    signal: AbortSignal,
  ) => Promise<void>;
}

/** The two writes the left dock's attachments panel asks for: files to embed, or name-tree keys to drop. */
export interface AttachmentWrite {
  readonly add?: readonly File[];
  readonly remove?: readonly string[];
}

/**
 * The embedded files the properties panel lists, each with its measured size. The engine's
 * attachment list carries names and descriptions but not payloads, so a size is the byte
 * length of the payload read one file at a time; an unreadable payload is `null`.
 */
export async function measuredAttachments(
  handle: PdfDocumentHandle,
  signal: AbortSignal,
): Promise<readonly AttachmentRow[]> {
  const measured: AttachmentRow[] = [];
  for (const attachment of await listPdfAttachments(handle)) {
    if (signal.aborted) break;
    const size = await readPdfAttachment(handle, attachment).then(
      (bytes) => bytes.byteLength,
      () => null,
    );
    measured.push({
      id: attachment.id,
      name: attachment.filename,
      description: attachment.description,
      size,
    });
  }
  return measured;
}

/** The active tab and the handle it renders, or `null` when either is missing. */
function activeDocument(session: SessionStore): { tab: SessionTab; handle: PdfDocumentHandle } | null {
  const tab = session.active;
  const handle = tab === null ? undefined : handleFor(tab.id);
  return tab === null || handle === undefined ? null : { tab, handle };
}

/**
 * Run `work` as an operation on the active document (`runGatedOperation`: busy gate, a Cancel
 * that aborts the `signal` it receives, a failure as a notice). Nothing runs without a tab and
 * a handle.
 */
async function withDocument(
  deps: AttachmentDeps,
  ready: () => boolean,
  work: (tab: SessionTab, handle: PdfDocumentHandle, signal: AbortSignal) => Promise<void>,
): Promise<void> {
  const document = activeDocument(deps.session);
  if (document === null || !ready()) return;
  await runGatedOperation(deps.t, (signal) => work(document.tab, document.handle, signal));
}

/** The bytes of picked files, ready for the writer to embed. */
function payloadsOf(files: readonly File[], mimeOf: (file: File) => string) {
  return Promise.all(
    files.map(async (file) => ({
      name: file.name,
      // `File.arrayBuffer` is the only read that does not need a URL or a reader.
      bytes: new Uint8Array(await file.arrayBuffer()),
      mime: mimeOf(file),
    })),
  );
}

/** The handlers behind the properties panel (add, remove, read out) and the attachments panel (write). */
/** What the dock panels ask of the attachments. */
export interface AttachmentActions {
  /** Embed the picked files in the document. */
  readonly addToDocument: (files: readonly File[]) => Promise<void>;
  /** Drop one embedded file by its name-tree key. */
  readonly removeFromDocument: (id: string) => Promise<void>;
  /** Save one embedded file out to disk. */
  readonly readOut: (name: string) => Promise<void>;
  /** Embed and drop in one journal step. */
  readonly write: (request: AttachmentWrite) => Promise<void>;
}

export function createAttachmentActions(deps: AttachmentDeps): AttachmentActions {
  const { t } = deps;

  /** Embed the picked files in the document. */
  async function addToDocument(files: readonly File[]): Promise<void> {
    await withDocument(
      deps,
      () => files.length > 0,
      async (tab, handle, signal) => {
        const base = await materializeBase(documentContext(deps.session, deps.t, tab, handle), { signal });
        const payloads = await payloadsOf(files, (file) =>
          file.type.length === 0 ? 'application/octet-stream' : file.type,
        );
        const outcome = await addAttachments(base, payloads, { signal });
        const next = await applyProducedBytes(
          documentContext(deps.session, deps.t, tab, handle),
          outcome.bytes,
          workingPageCount(tab),
          { key: 'props.attach.added', params: { count: outcome.added.length } },
          outcome.report.engine,
          outcome.report.steps,
          { signal },
        );
        swapHandle(deps.t, tab.id, next);
        showNotice(t('props.attach.added', { count: outcome.added.length }));
      },
    );
  }

  /** Drop the embedded file whose name-tree key is `id` from the document. */
  async function removeFromDocument(id: string): Promise<void> {
    await withDocument(
      deps,
      () => true,
      async (tab, handle, signal) => {
        const base = await materializeBase(documentContext(deps.session, deps.t, tab, handle), { signal });
        const outcome = await removeAttachments(base, [id], { signal });
        const next = await applyProducedBytes(
          documentContext(deps.session, deps.t, tab, handle),
          outcome.bytes,
          workingPageCount(tab),
          { key: 'props.attach.removed', params: { count: outcome.removed.length } },
          outcome.report.engine,
          outcome.report.steps,
          { signal },
        );
        swapHandle(deps.t, tab.id, next);
        showNotice(
          outcome.missing.length > 0
            ? t('props.attach.missing', { count: outcome.missing.length })
            : t('props.attach.removed', { count: outcome.removed.length }),
        );
      },
    );
  }

  /** Write one embedded file out — the only operation that never touches the document. */
  async function readOut(name: string): Promise<void> {
    const document = activeDocument(deps.session);
    if (document === null) return;
    try {
      const attachments = await listPdfAttachments(document.handle);
      const attachment = attachments.find((entry) => entry.filename === name);
      if (attachment === undefined) return;
      const bytes = await readPdfAttachment(document.handle, attachment);
      downloadFiles([{ name: attachment.filename, bytes, mime: 'application/octet-stream' }]);
      showNotice(t('props.attach.readNamed', { name: attachment.filename }));
    } catch (error) {
      showNotice(failureLine(error, t));
    }
  }

  /**
   * The attachments panel's two writes (“attachments add/remove”), run on the working
   * document through the shared writer pipeline: the panel hands over the picked files (or the
   * name-tree keys to drop) and the shell journals what the writer produced.
   */
  async function write(request: AttachmentWrite): Promise<void> {
    await withDocument(
      deps,
      () => true,
      async (tab, handle, signal) => {
        const bytes = await materializeBase(documentContext(deps.session, deps.t, tab, handle), { signal });
        const operation = { signal };
        let outcome: OperationOutcome;
        if (request.add !== undefined && request.add.length > 0) {
          // A browser that knows nothing about the type says `''`, and the writer stores
          // that as no `/Subtype`.
          outcome = await addAttachments(
            bytes,
            await payloadsOf(request.add, (file) => file.type),
            operation,
          );
        } else if (request.remove !== undefined && request.remove.length > 0) {
          outcome = await removeAttachments(bytes, request.remove, operation);
        } else {
          return;
        }
        await deps.applyWriterOutcome(tab, handle, outcome, 'panel.attachments', signal);
      },
    );
  }

  return { addToDocument, removeFromDocument, readOut, write };
}
